import { CSSProperties, useEffect, useRef, useState } from 'react';
import { Look, looks } from '../../data/home';
import Caption from './Caption';

const FEATURED = 3;
const LAYOUTS = ['sp-look--split', '', 'sp-look--right'];

const LookMedia = ({ look, index, className = '' }: { look: Look; index: number; className?: string }) => (
  <figure className={`sp-look-media${index === 0 ? ' sp-look-media--split' : ''}${className}`}>
    <picture>
      {look.image.desktop && <source media="(min-width: 900px)" srcSet={look.image.desktop} />}
      <img
        src={look.image.src}
        alt={look.image.alt}
        loading="lazy"
        decoding="async"
        style={{ '--pos-d': look.image.posD ?? '50% 50%', '--pos-m': look.image.posM ?? '50% 50%' } as CSSProperties}
      />
    </picture>
  </figure>
);

/**
 * The first three dishes, one per screen. With IntersectionObserver the
 * photos move into a single sticky frame and crossfade look to look while each
 * caption wipes in; without it (or before JS), every look keeps its own
 * photo and the page still reads top to bottom.
 */
const SpLooks = () => {
  const featured = looks.slice(0, FEATURED);
  const [staged, setStaged] = useState(false);
  const [active, setActive] = useState(0);
  const refs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;
    setStaged(true);
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(refs.current.indexOf(e.target as HTMLElement));
        }),
      { threshold: 0, rootMargin: '-50% 0px -49.9% 0px' },
    );
    refs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <section className={`sp-looks${staged ? ' is-staged' : ''}`} id="looks" aria-label="Signature dishes">
      {staged && (
        <div className="sp-looks-stage" aria-hidden="true">
          {featured.map((look, i) => (
            <LookMedia key={look.menuId} look={look} index={i} className={i === active ? ' is-on' : ''} />
          ))}
        </div>
      )}
      {featured.map((look, i) => (
        <article
          key={look.menuId}
          ref={(el) => {
            refs.current[i] = el;
          }}
          className={`sp-look ${LAYOUTS[i] ?? ''}${i === active ? ' is-on' : ''}`}
          aria-label={look.name}
        >
          <LookMedia look={look} index={i} />
          <Caption look={look} />
        </article>
      ))}
    </section>
  );
};

export default SpLooks;
